import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberLevelDto, TimedFeeTable } from '@pokerroom/types';
import { api } from '../../lib/api';
import { halfVenueFee } from './timedFeeDefaults';

export function TimedFeeEditor({
  value,
  principal,
  legacyRake,
  onChange,
}: {
  value: TimedFeeTable | undefined;
  principal: string;
  legacyRake: string;
  onChange(value: TimedFeeTable): void;
}) {
  const { t } = useTranslation('tournament');
  const { data: levels = [], isError } = useQuery({
    queryKey: ['timed-fee-member-levels'],
    queryFn: () =>
      api.get<Pick<MemberLevelDto, 'id' | 'displayName'>[]>(
        '/tournament/templates/fee-member-levels',
      ),
  });
  const draft: TimedFeeTable = value ?? {
    generalMemberLevelId: '',
    principalAmount: principal,
    originalVenueFeeAmount: '',
    generalReservationVenueFeeAmount: '',
    memberVenuePrices: [],
    repeatVenueFees: { reentry: '', rebuy: '', addon: '' },
    afterBreakVenueFeeAmount: '',
  };
  const field = (
    key: 'originalVenueFeeAmount' | 'generalReservationVenueFeeAmount' | 'afterBreakVenueFeeAmount',
    label: string,
  ) => (
    <label className="form-control">
      <span>{label}</span>
      <input
        className="input input-bordered"
        type="text"
        inputMode="decimal"
        value={draft[key]}
        onChange={(event) =>
          onChange({ ...draft, principalAmount: principal, [key]: event.target.value })
        }
        onBlur={() => {
          if (key !== 'originalVenueFeeAmount') return;
          const half = halfVenueFee(draft.originalVenueFeeAmount);
          if (half === null) return;
          const repeats = draft.repeatVenueFees;
          if (Object.values(repeats).some((value) => value === ''))
            onChange({
              ...draft,
              repeatVenueFees: {
                reentry: repeats.reentry || half,
                rebuy: repeats.rebuy || half,
                addon: repeats.addon || half,
              },
            });
        }}
      />
    </label>
  );
  return (
    <fieldset className="border border-base-300 rounded-lg p-4 space-y-3">
      <legend className="font-semibold">{t('timedFees.title')}</legend>
      {!value && <p>{t('timedFees.legacy', { rate: legacyRake })}</p>}
      <p>{t('timedFees.explanation')}</p>
      {isError && <p role="alert">{t('timedFees.levelsUnavailable')}</p>}
      <label className="form-control">
        <span>{t('timedFees.generalLevel')}</span>
        <select
          className="select select-bordered"
          value={draft.generalMemberLevelId}
          onChange={(event) =>
            onChange({
              ...draft,
              generalMemberLevelId: event.target.value,
              memberVenuePrices: draft.memberVenuePrices.filter(
                (row) => row.memberLevelId !== event.target.value,
              ),
            })
          }
        >
          <option value="">{t('timedFees.selectGeneral')}</option>
          {levels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.displayName}
            </option>
          ))}
        </select>
      </label>
      <div className="grid gap-3 md:grid-cols-2">
        {field('originalVenueFeeAmount', t('timedFees.original'))}
        {field('generalReservationVenueFeeAmount', t('timedFees.generalReservation'))}
        {field('afterBreakVenueFeeAmount', t('timedFees.afterBreak'))}
      </div>
      {levels
        .filter((level) => level.id !== draft.generalMemberLevelId)
        .map((level) => {
          const row = draft.memberVenuePrices.find((row) => row.memberLevelId === level.id);
          return (
            <div key={level.id} className="grid gap-3 md:grid-cols-2">
              {(['ordinaryAmount', 'reservationAmount'] as const).map((key) => (
                <label className="form-control" key={key}>
                  <span>
                    {level.displayName} · {t(`timedFees.${key}`)}
                  </span>
                  <input
                    className="input input-bordered"
                    type="text"
                    inputMode="decimal"
                    value={row?.[key] ?? ''}
                    onChange={(event) =>
                      onChange({
                        ...draft,
                        principalAmount: principal,
                        memberVenuePrices: [
                          ...draft.memberVenuePrices.filter(
                            (item) => item.memberLevelId !== level.id,
                          ),
                          {
                            memberLevelId: level.id,
                            ordinaryAmount: row?.ordinaryAmount ?? '',
                            reservationAmount: row?.reservationAmount ?? '',
                            [key]: event.target.value,
                          },
                        ],
                      })
                    }
                  />
                </label>
              ))}
            </div>
          );
        })}
      <div className="grid gap-3 md:grid-cols-3">
        {(['reentry', 'rebuy', 'addon'] as const).map((key) => (
          <label className="form-control" key={key}>
            <span>{t(`timedFees.${key}`)}</span>
            <input
              className="input input-bordered"
              type="text"
              inputMode="decimal"
              value={draft.repeatVenueFees[key]}
              onChange={(event) =>
                onChange({
                  ...draft,
                  principalAmount: principal,
                  repeatVenueFees: { ...draft.repeatVenueFees, [key]: event.target.value },
                })
              }
            />
            <span className="text-xs">{t('timedFees.samePrincipal', { amount: principal })}</span>
          </label>
        ))}
      </div>
      {halfVenueFee(draft.originalVenueFeeAmount) === null &&
        draft.originalVenueFeeAmount !== '' && <p>{t('timedFees.explicitRepeat')}</p>}
      <p className="text-sm">{t('timedFees.saveVersion')}</p>
    </fieldset>
  );
}
